import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs/internal/Observable';
import { distinctUntilChanged, startWith, take } from 'rxjs/operators';
import { SearchInputColorEnum } from 'src/app/shared/components/search-input/search-input.component';
import { IInventoryTotals } from 'src/app/shared/interfaces/IInventoryTotals';
import { SearchDateRange } from 'src/app/shared/interfaces/SearchDateRange';
import { DrugsService } from 'src/app/shared/services/drugs.service';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { PERIODS_LIST } from '../../../shared/constants/search.constants';
import { BaseTableDirective } from '../../../shared/directives/base-table.directive';
import { getFlatObject, getUTC, sortByProperty } from '../../../shared/helpers/helpers';
import { ActivityTypesEnum } from '../../../shared/interfaces/ActivityTypesEnum';
import { BadgeColorsEnum } from '../../../shared/interfaces/BadgeColorsEnum';
import { IDrug } from '../../../shared/interfaces/drug.interface';
import { AuthState } from '../../../shared/states/auth/auth.state';
import { GetDrugsForDrugsFilter } from '../../../shared/states/drugs/drugs.actions';
import { DrugsState } from '../../../shared/states/drugs/drugs.state';
import { ProfileState } from '../../../shared/states/profile/profile.state';
import {
  RunInventoryTotalsReport,
  SaveInventoryTotalsFormState,
  SetInventoryTotals,
  SetLoading,
} from '../../../shared/states/reports/reports.actions';
import { ReportsState } from '../../../shared/states/reports/reports.state';
import { ITeamRecord } from '../../../team/interfaces/team-record.interface';
import { ReportsService } from '../../reports.service';
import Decimal from 'decimal.js';

@UntilDestroy()
@Component({
  selector: 'app-inventory-snapshot-reports',
  templateUrl: './inventory-snapshot-reports.component.html',
  styleUrls: ['./inventory-snapshot-reports.component.scss'],
})
export class InventorySnapshotReportsComponent extends BaseTableDirective implements OnInit {
  inventoryTotalsForm: UntypedFormGroup;

  tableColumns = {
    starting: true,
    friendlyName: true,
    received: true,
    dispensed: true,
    lostStolen: true,
    damagedExpired: true,
    movedFrom: true,
    movedTo: true,
    soldToProvider: true,
    soldToReverseDistributor: true,
    returned: true,
    destroyed: true,
    ending: true,
    locationName: true,
    mixed: true
  };

  periodsList = [];

  inventoryTotals: IInventoryTotals[] = [];
  sortedRecordsList: any[] = [];

  isReportRunApplied: boolean;

  drugItemsShowLimit = 2;

  isDrugsDropdownOpened: boolean;
  isFileDownloading: boolean;

  drugsList = [];

  drugListSettings = {
    selectAllText: 'All Drugs',
    unSelectAllText: 'Deselect All',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Drugs',
    noDataAvailablePlaceholderText: 'No drugs available',
  };

  originalTitle = 'DEA-Compliant Inventory Totals Report';

  title = this.originalTitle;
  private schedulesInReport: number[];

  ActivityTypesEnum = ActivityTypesEnum;
  BadgeColorsEnum = BadgeColorsEnum;
  SearchInputColorEnum = SearchInputColorEnum;

  groupResultsBy = 'drug';
  groupItemsOpened: boolean[] = [];

  @Select(ReportsState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(ReportsState.inventoryTotals) inventoryTotals$: Observable<IInventoryTotals[]>;
  @Select(ReportsState.runningReports) runningReports$: Observable<boolean>;
  @Select(ReportsState.inventoryTotalsForm) inventoryTotalsForm$: Observable<any>;
  @Select(DrugsState.drugsFilterDrugItems) drugsFilterDrugItems$: Observable<IDrug[]>;
  @Select(ProfileState.teamList) teamList$: Observable<ITeamRecord[]>;
  @Select(InventoryState.loadingLibertyRxInventory) loadingLibertyRxInventory$: Observable<boolean>;
  @Select(PharmacyState.enableC3toC5Drugs) enableC3toC5Drugs$: Observable<boolean>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;
  @Select(PharmacyState.compoundingEnabled) compoundingEnabled$: Observable<boolean>;


  @ViewChild('periodDropdown') periodDropdown;
  @ViewChild('customPeriodDatepicker') customPeriodDatepicker;
  @ViewChild('drugsMultioptionalDropdown') drugsMultioptionalDropdown;
  @ViewChild('downloadMenu') downloadMenu;

  inventoryLocationsEnabled: boolean;

  constructor(
    protected store: Store,
    private fb: UntypedFormBuilder,
    private datePipe: DatePipe,
    private reportsService: ReportsService,
    private drugService: DrugsService
  ) {
    super(store);

    this.inventoryTotalsForm = this.fb.group({
      period: ['', []],
      customPeriod: [null, []],
      drugs: [[], []],
      dateRangeWording: '',
      schedules: null,
      searchValue: null,
      inventoryLocations: null
    });

    this.periodsList = [
      {id: SearchDateRange.Custom, name: this.getCustomRangeOptionName(), value: undefined},
      ...PERIODS_LIST,
    ];
  }

  ngOnInit(): void {
    this.inventoryTotals$.pipe(untilDestroyed(this))
      .subscribe((records: IInventoryTotals[]) => {
        if (records && records.length) {
          this.inventoryTotals = records;
          if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
            this.filterBySearchValue(this.inventoryTotalsForm.get('searchValue').value);
          }
        } else {
          this.inventoryTotals = [];
        }
        this.schedulesInReport = this.drugService.getSchedulesBasedOnExistingInventories(this.inventoryTotals as any);
        this.changeTitle(this.schedulesInReport || []);
      });

    this.store.dispatch(new GetDrugsForDrugsFilter());

    this.drugsFilterDrugItems$.pipe(untilDestroyed(this))
      .subscribe((data: IDrug[]) => {
        this.populateDrugFilter(data, this.inventoryTotalsForm.value?.schedules?.map(x => x.id));
      });

    this.inventoryTotalsForm$.pipe(take(1))
      .subscribe(data => {
        this.inventoryTotalsForm.patchValue(data);
      });

    this.inventoryTotalsForm.get('schedules')?.valueChanges
    .pipe(
      untilDestroyed(this),
    )
    .subscribe((schedules: any) => {
      this.populateDrugFilter(this.store.selectSnapshot(DrugsState)?.drugsFilterDrugItems, schedules?.map(x => x.id));
    });

    this.enableC3toC5Drugs$.subscribe(enabled => {
      if (!enabled) {
        this.inventoryTotalsForm.get('schedules').reset();
      }
    });

    this.inventoryTotalsForm.get('searchValue')?.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(''),
        distinctUntilChanged(),
      )
      .subscribe((search: string) => {
        this.filterBySearchValue(search);
      });

      this.inventoryLocationsEnabled$
      .pipe(untilDestroyed(this))
      .subscribe(val => this.inventoryLocationsEnabled = val);
  }

  changeTitle(existingSchedules: number[]): void {
    this.title = this.reportsService.composeReportTitleWithScheduleExtension(this.originalTitle, [...existingSchedules || []]);
  }

  focusMultioptionalDropdown(dropdownName: string): void {
    setTimeout(() => {
      this[dropdownName].opened = true;
      this[dropdownName].isFocused = true;
      this[dropdownName].dropDownOpened.emit();
      this[dropdownName].multiselectDropdown.nativeElement.focus();
    });
  }

  reset(): void {
    this.inventoryTotalsForm.reset();
    this.resetCustomRange();
    this.inventoryTotalsForm.get('schedules').setValue([])
    this.store.dispatch(new SaveInventoryTotalsFormState(null));
    this.inventoryTotals = [];
    this.store.dispatch(new SetInventoryTotals(this.inventoryTotals));
  }

  runReport(): void {
    const raw = this.inventoryTotalsForm.getRawValue();
    const customDateRange = this.periodsList.find(x => x.id === SearchDateRange.Custom).value;
    this.store.dispatch(new RunInventoryTotalsReport({
      startDate: customDateRange ? 
        customDateRange.startDate?.toDateString() : raw?.customPeriod?.[0] ?? null,
      endDate: customDateRange?.endDate ?
        customDateRange?.endDate.toDateString() : raw?.customPeriod?.[1] ?? null,
      drugIds: raw?.drugs?.map(x => x.id) || [],
      pharmacyId: this.store.selectSnapshot(AuthState.pharmacyId),
      searchDateRange: !raw?.period ? 0 : raw.period,
      offset: new Date().getTimezoneOffset() / 60,
      schedules: raw?.schedules?.map(x => x.id),
      locations: raw?.inventoryLocations?.map(x => x.id)
    }))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (raw.customPeriod?.length || raw.period) {
          const from = this.datePipe.transform(new Date(getUTC(this.store.selectSnapshot(ReportsState)?.inventoryTotalsFrom)), 'MMM dd, yyyy');
          const to = this.datePipe.transform(new Date(getUTC(this.store.selectSnapshot(ReportsState)?.inventoryTotalsTo)), 'MMM dd, yyyy');
          let dateRangeWording = "from " + from + " - " + to;
          this.inventoryTotalsForm.get('dateRangeWording').patchValue(dateRangeWording);
        }
        this.store.dispatch(new SaveInventoryTotalsFormState(this.inventoryTotalsForm.getRawValue()));
      });
    this.isReportRunApplied = true;
  }

  saveCSV(): void {
    this.store.dispatch(new SetLoading(true));
    this.isFileDownloading = true;
    
    const payload = {totals: this.inventoryTotals, title: this.title, withInventoryLocations: this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)};

    this.reportsService.downloadInventoryCSVTotalsReport(payload)
      .pipe(take(1))
      .subscribe(res => {
        const name = this.title + " " + this.inventoryTotalsForm.getRawValue()?.dateRangeWording + '.csv';
        this.handleDownload(res, name);
      }, err => {
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      });
  }

  savePDF(): void {
    this.store.dispatch(new SetLoading(true));
    this.isFileDownloading = true;
    const customDateRange = this.periodsList.find(x => x.id === SearchDateRange.Custom)?.value;
    const raw = this.inventoryTotalsForm.getRawValue();
    const payload = {totals: this.inventoryTotals,
      title: this.title,
      startDate: customDateRange?.startDate?.toDateString(),
      endDate: customDateRange?.endDate?.toDateString(),
      searchDateRange: !raw?.period ? 0 : raw.period,
      withInventoryLocations: this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)
    };
    this.reportsService.downloadInventoryPDFTotalsReport(payload)
      .pipe(take(1))
      .subscribe(res => {
        const name = `${this.title} ${this.inventoryTotalsForm.getRawValue()?.dateRangeWording || ''}.pdf`;
        this.handleDownload(res, name);
      }, err => {
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      });
  }

  handleDownload(res: Blob, fileName: string): void {
    const downloadedFile = new Blob([res], {type: res.type});
    const a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);
    a.download = fileName;
    a.href = URL.createObjectURL(downloadedFile);
    a.target = '_blank';
    a.click();
    document.body.removeChild(a);
    this.store.dispatch(new SetLoading(false));
    this.isFileDownloading = false;
  }

  changePeriod(period): void {
    if (period && period.id === SearchDateRange.Custom) {
      setTimeout(() => this.customPeriodDatepicker.datepickerInstance?.open());
    } else {
      this.resetCustomRange();
    }
  }

  areFiltersEmpty(): boolean {
    const formValue = this.inventoryTotalsForm.getRawValue();
    return Object.values(formValue).every((x: any) => !x || (Array.isArray(x) && !x.length));
  }

  customPeriodChanged(date): void {
    this.inventoryTotalsForm.get('customPeriod').patchValue([this.datePipe.transform(date.value[0], 'M/d/yyyy'), this.datePipe.transform(date.value[1], 'M/d/yyyy')]);
    const custom = this.periodsList.find(x => x.id === SearchDateRange.Custom);
    custom.name =
      `Custom (${this.datePipe.transform(date.value[0], 'M/d/yyyy')} - ${this.datePipe.transform(date.value[1], 'M/d/yyyy')})`;
    custom.value = {startDate: date.value[0], endDate: date.value[1]};
    this.periodDropdown.chooseOption(custom);
  }

  checkDateRangeAfterClosed(): void {
    if (this.periodsList.find(x => x.id === SearchDateRange.Custom).name === 'Custom Range') {
      this.periodDropdown.chooseOption(this.periodsList.find(x => x.id === SearchDateRange.Custom));
    }
  }

  multiselectOpened(limitName: string, dropdownShowName: string): void {
    setTimeout(() => {
      this.drugItemsShowLimit = 1;
      this.isDrugsDropdownOpened = false;
      this[limitName] = 5;
      this[dropdownShowName] = true;
    });
  }

  multiselectClosed(limitName: string, dropdownShowName: string): void {
    this.drugItemsShowLimit = 2;
    this[dropdownShowName] = false;
  }

  getTodayDate() {
    return new Date();
  }


  sortByColumn(columnName: string): void {
    this.store.dispatch(new SetInventoryTotals(this.sort(this.inventoryTotals, columnName)));
  }

  trackByFn(index, item): number | string {
    return item.friendlyName;
  }

  private getCustomRangeOptionName(): string {
    const customPeriod = this.store.selectSnapshot(ReportsState)?.inventoryTotalsForm?.customPeriod;

    if (!!customPeriod?.length) {
      return `Custom (${this.datePipe.transform(customPeriod[0], 'M/d/yyyy')} - ${this.datePipe.transform(customPeriod[1], 'M/d/yyyy')})`
    }
    return 'Custom Range';
  }

  private populateDrugFilter(drugs: IDrug[], schedules: number[]): void {
    if (this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      schedules = !schedules?.length ? this.drugService.getSchedulesList() : schedules;
    }
    let filteredBySchedule = [];
    if (this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      filteredBySchedule = drugs?.filter(x => schedules?.includes(x.schedule)) || [];
    } else filteredBySchedule = drugs || [];

    this.drugsList = filteredBySchedule.map(el => ({id: el.id, name: el.friendlyName, itemName: el.friendlyName, code: el.packageNdc}));
    this.drugsList = sortByProperty(this.drugsList, 'name', true);
  }

  resetCustomRange(): void {
    const customRange = this.periodsList.find(x => x.id === SearchDateRange.Custom);
    customRange.name = 'Custom Range';
    (customRange as any).value = null;
    this.customPeriodDatepicker.reset()
 
   }

   filterBySearchValue(searchString: string = ''): void {
    searchString = searchString?.replace(/-/g, '');
    const lowerCaseSearchStr = searchString?.toLocaleLowerCase();
    const filterFunction = (search, data) => searchString ?
      data.filter(el => 
        el.friendlyName.toLowerCase().toString().includes(lowerCaseSearchStr) ||
        el.packageNdc.replace(/-/g, '').includes(search)) : data;
    const filtered = filterFunction(searchString, [...this.inventoryTotals]);
    this.updateSortedList(filtered);
  }

  updateSortedList(source: IInventoryTotals[]): void {
    const flatList = [...source].map((transaction: IInventoryTotals) => {
      const flatTransaction = getFlatObject({...transaction, history: null});
      switch (this.groupResultsBy) {
        case 'drug':
          flatTransaction.drug = `${flatTransaction['friendlyName']} (${flatTransaction['packageNdc']})`;
          break;
      }

      return flatTransaction;
    });
    setTimeout(() => {
      this.sortedRecordsList = this.groupByProperty(flatList, this.groupResultsBy);
      this.groupItemsOpened = Array(this.sortedRecordsList.length).fill(true);
      this.store.dispatch(new SetLoading(false));
    });
  }

  onScroll(ev: Event): void {
    document.querySelector('tbody').scrollTop = (ev.target as any).scrollTop;
  }

  private groupByProperty(array: any[], key: string): any[] {
    const sortObjByProperty = array.reduce((rv, x) => {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
    return Object.entries(sortObjByProperty).map(([k, v]) => ({header: k, totals: v, 
      totalPerGroup: (v as any[])?.map(x => x?.ending)?.reduce( (accumulator, currentValue) => new Decimal(accumulator).add(new Decimal(currentValue)))}));
  }

}
